// @figma file=pkCGby54zXzTsccXvHqzO6 node=3830:58457 name="✦ Radio" set="mytaible-shadcn-studio" verified=2026-03-21

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { Circle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── RadioGroup ───────────────────────────────────────────────────────────────

export interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  /**
   * Layout direction of the radio items.
   * vertical (default) → stacked list
   * horizontal → inline row
   */
  orientation?: 'horizontal' | 'vertical';
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn(
      'flex',
      orientation === 'horizontal' ? 'flex-row gap-6' : 'flex-col gap-2',
      className,
    )}
    {...props}
  />
));
RadioGroup.displayName = 'RadioGroup';

// ─── RadioGroupItem variants ──────────────────────────────────────────────────

const radioGroupItemVariants = cva(
  [
    'aspect-square rounded-full border border-border bg-background',
    'ring-offset-background transition-colors',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
    // Checked: border + text inherit the --radio-color CSS custom property
    'data-[state=checked]:border-[var(--radio-color)] data-[state=checked]:text-[var(--radio-color)]',
    // Hover on unchecked: subtle border shift
    'hover:border-[var(--radio-color)]/60',
  ].join(' '),
  {
    variants: {
      /**
       * Size — matches Figma `Size` dimension.
       * sm → 16×16 (Figma sm)
       * md → 20×20 (Figma md) — default
       * lg → 24×24 (Figma lg)
       */
      size: {
        sm: 'size-4',
        md: 'size-5',
        lg: 'size-6',
      },
      /**
       * Color intent — matches Figma `Variant` dimension.
       * Sets --radio-color to the corresponding semantic token.
       */
      variant: {
        primary: '[--radio-color:var(--color-primary)]',
        secondary: '[--radio-color:var(--color-secondary-foreground)]',
        success: '[--radio-color:var(--color-success)]',
        warning: '[--radio-color:var(--color-warning)]',
        info: '[--radio-color:var(--color-info)]',
        destructive: '[--radio-color:var(--color-destructive)]',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'primary',
    },
  },
);

// ─── RadioGroupItem ───────────────────────────────────────────────────────────

export interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupItemVariants> {}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, size, variant, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(radioGroupItemVariants({ size, variant }), className)}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      {/* Inner dot — size matches Figma: 8/12/16px for sm/md/lg */}
      <Circle
        aria-hidden
        className={cn(
          'fill-current stroke-none',
          size === 'sm' ? 'size-2' : size === 'lg' ? 'size-4' : 'size-3',
        )}
      />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioGroupItem.displayName = 'RadioGroupItem';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { RadioGroup, RadioGroupItem, radioGroupItemVariants };
